import classNames from 'classnames';
import { differenceInDays } from 'date-fns';
import { useRouter } from 'next/router';
import { type FC, useEffect, useState } from 'react';

import BroadcastContainer from '@/components/BroadcastContainer';
import Buttons from '@/components/Buttons';
import Chips from '@/components/Chips';
import FilterChip from '@/components/FilterChip';
import Broadcast from '@/components/Icons/Broadcast';
import FunnelSimple from '@/components/Icons/FunnelSimple';
import LockOpen from '@/components/Icons/LockOpen';
import StartPostButton from '@/components/StartPostButton';
import { UsersAvatarGroup } from '@/containers/UsersAvatarGroup';
import formatParticipants from '@/helpers/formatParticipants';
import { IDENTIFIERS, getUrl } from '@/helpers/urlsHelper';
import { useIsMobile, useUser } from '@/hooks';
import useNoodleApi from '@/hooks/useNoodleApi';
import CreateBroadcastModal from '@/modals/CreateEditBroadcastModal';
import { JobStatus, JobType, useJobContext } from '@/providers/Jobs';
import { mixpanelTrack } from '@/providers/Mixpanel';
import { SIZES } from '@/styles/media';
import * as tsClient from '@/tsClient';
import { type MediaApiModels } from '@/tsClient/servers/noodleMedia';
import { type Message } from '@/typings/api-models';
import { type DefinitelyFunctionReturn } from '@/typings/utility';

import type { ThisProduct } from '../CreatorBySlug';

import s from './Broadcasts.module.scss';

type Participant = NonNullable<Parameters<typeof UsersAvatarGroup>[0]['users']>[number];

export type Comments = Array<
  Pick<Message, 'id' | 'title' | 'text' | 'isDeleted' | 'createdAt' | 'type'> & {
    parent?:
      | (Pick<Message, 'id'> & {
          owner?: {
            name?: string | null;
            image?: { url: string } | null;
          } | null;
        })
      | null;
    owner?: {
      name?: string | null;
      image?: { url: string } | null;
    } | null;
    tags: {
      id: string;
      name: string;
    }[];
    medias: Pick<MediaApiModels.Media, 'id' | 's3OriginId' | 'vimeoThumbnailUrl' | 'vimeoUri' | 'type'>[];
    children: Array<{
      owner?: {
        id: string;
        name?: string | null;
        image?: { url: string } | null;
      } | null;
    }>;
  }
>;

type LatestBroadcastType = {
  howMany?: number;
  comments: Comments | undefined;
  creatorSlug: string;
  broadcastSlug: string | null;
};

const LatestBroadcast: React.FC<LatestBroadcastType> = ({ howMany, comments, creatorSlug, broadcastSlug }) => {
  if (comments) {
    return (
      <BroadcastContainer
        key={'Broadcasts'}
        title={'Broadcasts'}
        allBroadcastLink={getUrl(IDENTIFIERS.CREATOR, { creatorSlug, tab: 'Broadcasts' })}
        creatorSlug={creatorSlug}
        howMany={howMany}
        isList
        shouldNavigate={false}
        data={comments.map((comment) => ({
          createdAt: comment.createdAt,
          creatorSlug,
          enableLink: true,
          hideDate: false,
          id: comment.id,
          inContainer: true,
          isNew: differenceInDays(new Date(), new Date(comment.createdAt)) < 2,
          media: comment.medias?.[0],
          messageId: comment?.id || null,
          numComments: comment.children.length,
          participants: formatParticipants(comment),
          productSlug: broadcastSlug,
          text: comment.text,
          title: comment.title || 'Untitled Broadcast',
        }))}
      />
    );
  }
  return null;
};

const Broadcasts: FC<{
  membershipProducts: ThisProduct[];
  broadcast: ThisProduct | undefined;
  broadcastComments: ThisProduct['comments'];
  broadcastTiers: Parameters<typeof CreateBroadcastModal>[0]['broadcastTiers'];
  canEdit: boolean;
  creator: Awaited<ReturnType<typeof tsClient.getCreator>>;
  refetchData: () => Promise<void>;
}> = ({ membershipProducts, canEdit, broadcast, broadcastComments, broadcastTiers, creator, refetchData }) => {
  const [isUnlockClicked, setIsUnlockClicked] = useState(false);
  const [canComment, setCanComment] = useState(false);
  const [user] = useUser();
  const [broadcastsOnly, setBroadcastsOnly] = useState(true);
  const router = useRouter();
  const [selectedChip, setSelectedChip] = useState<string | null>('View All');
  const queryTag = router.query.tag;
  const isMobile = useIsMobile(SIZES.md);

  const { jobs } = useJobContext();

  const isDisabled = Boolean(
    jobs.find((j) => j.type === JobType.CREATE_DEFAULT_PRODUCTS && j.status === JobStatus.IN_PROGRESS),
  );

  const [isCreateBroadcastModalVisible, setIsCreateBroadcastModalVisible] = useState(false);

  const {
    getData: getCommentPermissionsFn,
    fetchingState: { isFetching: isCheckingCanComment },
  } = useNoodleApi(tsClient.getCommentPermissions);
  const { getData: getMembersFn } = useNoodleApi(tsClient.creatorMembers.getMembers);

  const [members, setMembers] = useState<null | {
    total: number;
    items: Participant[];
  }>(null);

  const openCreateBroadcastModal = (): void => {
    mixpanelTrack('Edit Mode - message your community');
    setIsCreateBroadcastModalVisible(true);
  };

  const membershipProductId = membershipProducts[0]?.id;

  useEffect(() => {
    if (canEdit) {
      setBroadcastsOnly(false);
    }
  }, [canEdit]);

  useEffect(() => {
    if (Array.isArray(queryTag)) {
      setSelectedChip(queryTag[0]);
    }
    if (typeof queryTag === 'string') {
      setSelectedChip(queryTag);
    }
  }, [queryTag]);

  const setCreateBroadcastModalVisible = (isVisible: boolean): void => {
    setIsCreateBroadcastModalVisible(isVisible);
  };

  useEffect(() => {
    const checkCanComment = async (): Promise<void> => {
      if (membershipProductId && user && user.id && !user.isAnonymous) {
        const { data: commentData } = await getCommentPermissionsFn({ productId: membershipProductId });
        setCanComment(commentData?.canComment || false);
      } else {
        setCanComment(false);
      }
    };
    checkCanComment();
  }, [membershipProductId, user, getCommentPermissionsFn]);

  useEffect(() => {
    const getMembersCall = async (): Promise<void> => {
      if (creator) {
        const newData = await Promise.all(
          ['all'].map(async (key) => {
            const keyData = await getMembersFn({
              creatorSlug: creator.slug,
              filter: key,
              page: 1,
              perPage: 10,
              search: '',
              withNames: true,
            });
            return {
              ...keyData,
              key,
            };
          }),
        );
        const items: NonNullable<DefinitelyFunctionReturn<typeof getMembersFn>['data']>['items'] = [];
        let total = 0;
        newData.forEach((item) => {
          items.push(...(item?.data?.items || []));
          total += item.data?.numItems || 0;
        });
        setMembers({ items: items.map((i) => ({ ...i, color: i?.primaryColour?.hex, logo: i.image?.url })), total });
      }
    };

    getMembersCall();
  }, [creator, getMembersFn]);

  const broadcastTags = Array.from(
    new Set(broadcastComments?.map((comment) => comment?.tags?.map((tag) => tag.name)).flat()),
  );
  const filteredBroadcastsData = broadcastComments?.filter((comment) =>
    comment.tags?.map((tag) => tag.id).includes(selectedChip as string),
  );
  const broadcastData = selectedChip === 'View All' ? broadcastComments : filteredBroadcastsData;

  return (
    <>
      {isCreateBroadcastModalVisible && creator && (
        <CreateBroadcastModal
          setIsVisible={setCreateBroadcastModalVisible}
          onPublish={async () => {
            await refetchData();
            setCreateBroadcastModalVisible(false);
          }}
          broadcastTiers={broadcastTiers}
          isCommunityPost={!canEdit}
          creatorSlug={creator.slug}
          productSlug={broadcast?.slug || null}
        />
      )}
      {creator && !canEdit && !isCheckingCanComment && membershipProducts.length > 0 && (
        <>
          {canComment ? (
            <StartPostButton onClick={openCreateBroadcastModal} />
          ) : (
            <>
              {members && (
                <UsersAvatarGroup
                  users={members?.items || []}
                  maxLength={5}
                  totalParticipants={members.total}
                  size={6}
                  isJoin
                />
              )}
              <Buttons
                isFourth
                onClick={() => {
                  router.push(
                    getUrl(IDENTIFIERS.PRODUCT, { creatorSlug: creator.slug, productSlug: membershipProducts[0].slug }),
                  );
                  setIsUnlockClicked(true);
                }}
                isShimmering={isUnlockClicked}
                iconBefore={<LockOpen weight='fill' size={20} />}
              >
                Unlock full community access
              </Buttons>
            </>
          )}
          <div className={s.chips}>
            <FilterChip
              label={`${creator.name}'s broadcasts only`}
              onClick={() => setBroadcastsOnly(!broadcastsOnly)}
              isSelected={broadcastsOnly}
              Icon={FunnelSimple}
            />
            {!!broadcastTags.length && (
              <Chips
                data={[
                  { id: 'View All', label: 'View All' },
                  ...broadcastTags.map((tag) => ({ id: tag, label: tag || '' })),
                ]}
                selectedItem={selectedChip}
                onItemClick={(e) => {
                  setSelectedChip(e);
                  router.push(
                    {
                      pathname: router.pathname,
                      query: { ...router.query, tab: 'Broadcasts', tag: e },
                    },
                    undefined,
                    { shallow: true },
                  );
                }}
              />
            )}
          </div>
        </>
      )}
      {creator && broadcastComments && broadcastComments.length > 0 && (
        <LatestBroadcast
          howMany={isMobile ? 4 : 6}
          comments={broadcastData?.filter((comment) => (broadcastsOnly ? comment.owner?.name === creator.name : true))}
          creatorSlug={creator.slug}
          broadcastSlug={broadcast?.slug || null}
        />
      )}
      {broadcastComments && broadcastComments.length === 0 && (
        <div className={classNames(s.noBroadcasts, 'body-sm')}>
          {canEdit
            ? 'You have no broadcasts yet. Message your community and get started!'
            : 'No broadcasts yet. Stay tuned!'}
        </div>
      )}
      {canEdit && (
        <Buttons isSecondary onClick={openCreateBroadcastModal} disabled={isDisabled} className={s.messageCommunity}>
          <Broadcast weight='fill' size={20} />
          Message Your Community
        </Buttons>
      )}
    </>
  );
};

export default Broadcasts;
